import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Phone, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clinicInfo } from '../mock';

const AfterHours = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-[#006D73] text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
              Same Day Appointments Available
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#000053] mb-6">
              Our After Hours - Same Day Clinic
            </h1>
            <p className="text-xl text-[#36454F] mb-8">
              We are here to assist you with your urgent health issues
            </p>
            <a href={`tel:${clinicInfo.phone}`}>
              <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all transform hover:scale-105">
                <Phone className="mr-2 h-5 w-5" />
                Call {clinicInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              Our after hours – same day clinic gives you the option to see a physician for your urgent health related issues.
            </p>

            <Card className="border-2 border-[#006D73] mb-12">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <AlertCircle className="h-8 w-8 text-[#006D73] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-[#000053] mb-3">Skip the Walk-In Clinic</h3>
                    <p className="text-gray-700 mb-4">
                      Instead of going to a walk-in clinic, call us the same day to book an appointment.
                    </p>
                    <p className="text-gray-700">
                      You will be able to see one of the physicians who is working that day.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hours */}
            <div className="bg-gray-50 rounded-lg p-8 mb-12">
              <div className="flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-[#006D73] mr-3" />
                <h2 className="text-2xl font-bold text-[#000053]">After Hours Clinic Schedule</h2>
              </div>
              <div className="max-w-md mx-auto space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm">
                  <span className="font-semibold text-[#36454F]">Monday to Thursday</span>
                  <span className="text-[#006D73] font-bold">5:00 PM - 8:00 PM</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm">
                  <span className="font-semibold text-[#36454F]">Saturday</span>
                  <span className="text-[#006D73] font-bold">9:00 AM - 12:00 PM</span>
                </div>
              </div>
            </div>

            {/* How it Works */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#000053] mb-6 text-center">How It Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="text-center border-2 hover:border-[#006D73] transition-all">
                  <CardContent className="p-6">
                    <div className="h-16 w-16 bg-[#006D73] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Phone className="h-8 w-8 text-[#006D73]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#000053] mb-2">1. Call Same Day</h3>
                    <p className="text-gray-600 text-sm">
                      Contact us the same day you need to be seen at {clinicInfo.phone}
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center border-2 hover:border-[#006D73] transition-all">
                  <CardContent className="p-6">
                    <div className="h-16 w-16 bg-[#006D73] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="h-8 w-8 text-[#006D73]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#000053] mb-2">2. Book Appointment</h3>
                    <p className="text-gray-600 text-sm">
                      We'll schedule you for an after-hours time slot that works for you
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center border-2 hover:border-[#006D73] transition-all">
                  <CardContent className="p-6">
                    <div className="h-16 w-16 bg-[#006D73] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-[#006D73]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#000053] mb-2">3. See a Physician</h3>
                    <p className="text-gray-600 text-sm">
                      Get professional medical care from one of our experienced physicians
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#000053]">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Need Urgent Care Today?
            </h2>
            <p className="text-gray-300 mb-8 text-lg">
              Call us the same day to book an appointment
            </p>
            <a href={`tel:${clinicInfo.phone}`}>
              <Button size="lg" className="bg-[#006D73] text-white hover:bg-[#005a5f] transition-all transform hover:scale-105 text-lg px-8 py-6">
                <Phone className="mr-2 h-6 w-6" />
                Call {clinicInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfterHours;